/**
 * The importer UI — one build, shared by both published plugins.
 *
 * Which plugin a given window belongs to isn't known until the sandbox
 * handshakes, so the branding is read from the store's variant rather than
 * hard-coded here. See src/variants.ts for why there are two of them and only
 * one copy of the code.
 */

import { ImporterFlow, LicensePanel } from '@figma-importers/ui';
import { licensing, parseRange, useImportStore } from './store/importStore';
import { ModePicker } from './components/ModePicker';
import { ArtboardPreview } from './components/ArtboardPreview';

function App() {
  const variant = useImportStore((s) => s.variant);
  const user = useImportStore((s) => s.user);
  const gateBlocked = useImportStore((s) => s.gateBlocked);
  const openLicenseLink = useImportStore((s) => s.openLicenseLink);
  const probe = useImportStore((s) => s.probe);
  const mode = useImportStore((s) => s.mode);
  const selection = useImportStore((s) => s.selection);
  const range = useImportStore((s) => s.range);
  const setSelection = useImportStore((s) => s.setSelection);

  // What the file turned out to contain, once the probe has answered. Everything below is
  // absent until then, which is why each is optional on the flow's config rather than a
  // placeholder: a count that says "0 artboards" while it is still counting is worse than
  // no count at all.
  const boards = probe?.pageCount ?? 0;
  const boardWord = boards === 1 ? 'artboard' : 'artboards';

  /*
   * How many artboards the CURRENT CHOICE would bring in, which is not the same as how many
   * the file holds. pdf names this number twice — "Importing 3 pages" under the footer and
   * "Import 3 pages" on the button — and both follow the selection, so picking "First page"
   * changes them to 1. A button that says "Import 3" while the radio says "First" is the
   * kind of disagreement nobody reads twice; they just press it.
   */
  const picked =
    boards === 0
      ? 0
      : selection === 'first'
        ? 1
        : selection === 'range'
          ? parseRange(range, boards).length
          : boards;
  const pickedWord = picked === 1 ? 'artboard' : 'artboards';

  /*
   * Whether this selection costs an upgrade, decided here rather than discovered on press.
   * Image mode is free on every plan and must never say otherwise — it is the fallback the
   * refusal itself suggests.
   */
  const gate = useImportStore((s) => s.gate);
  const limit = gate.pageLimit;
  const needsPro =
    mode !== 'image' && Number.isFinite(limit) && picked > limit && gate.plan !== 'pro';

  return (
    <ImporterFlow
      store={useImportStore}
      config={{
        app: variant.key,
        accept: variant.accept,
        dropHint: variant.dropHint,
        dropSubhint: 'We’ll turn it into a Figma design with editable layers, text and colors.',
        /*
         * The `?` menu every other plugin in the family shows. "Manage license" is left out
         * rather than shown inert: this app has no licence dialog to open, and the licence
         * panel already sits in the settings below.
         */
        help: {
          userId: user?.id ?? null,
          onSendFeedback: () => openLicenseLink('feedback'),
        },
        // pdf names the count on the button once there is more than one to name.
        importLabel: needsPro
          ? 'Unlock with Pro'
          : picked > 1
            ? `Import ${picked} ${pickedWord}`
            : 'Import',
        // The file bar's right-hand note, the way pdf shows "12 pg".
        fileMeta: boards > 0 ? `${boards} ${boardWord}` : undefined,
        preview: <ArtboardPreview />,
        summary: needsPro
          ? `Importing ${picked === boards ? 'all ' : ''}${picked} ${pickedWord} needs Pro.`
          : picked > 0
            ? `Importing ${picked} ${pickedWord}${mode === 'image' ? ' as images' : ''}`
            : undefined,
        /*
         * When the button has become "Unlock with Pro", the import the plan DOES allow has
         * to stay reachable, or paying is the only way forward. Selecting "First artboard"
         * is one click away in the list above, but the footer is where the refusal is read,
         * so the way out belongs beside it.
         */
        secondaryAction: needsPro
          ? {
              label: `Import ${limit} ${limit === 1 ? 'artboard' : 'artboards'} free`,
              onClick: () => setSelection('first'),
            }
          : undefined,
        unitNoun: 'artboard',
        settings: (
          <>
            <ModePicker />
            <LicensePanel
              licensing={licensing}
              user={user}
              onOpenLink={openLicenseLink}
              blocked={gateBlocked}
            />
          </>
        ),
      }}
    />
  );
}

export default App;
